/**
 * @fileoverview Client Selections Page
 *
 * Displays client home selections organized into tabs:
 * - Overview Tab: 4 sections (Exterior, Interior, Trim, Interior Features)
 *   matching the admin portal HomeSelections structure
 *   NOW WITH INLINE EDITING per section
 * - Paint Tab: Paint color selections with add/remove capability
 * - Owner Purchases Tab: Owner-purchased materials with add capability
 * - Documents Tab: Selection documents organized by category
 *
 * Uses conditional logic matching admin portal to show/hide fields
 * and mark missing selections. Data flows from the shared
 * selectionsService via useClientSelectionsData hook.
 *
 * Location: src/pages/client/ClientSelections.js
 *
 * @version 5.2.0
 * @updated 2026-03-12 - Added "view options" eye icons (FaEye) on edit-mode
 * fields linking to supplier sites or opening image/link modals via InfoModal.
 * Fields: Roof Color, Gutter Color, FCB Option, Door Pattern, Flooring
 * Direction, Painted Closet Shelves.
 */

import React, { useState, useRef, useEffect } from "react";
import { useClientProject } from "../../hooks";
import {
  useClientSelectionsData,
  useMarkSelectionRemoved,
  useUpdateClientSelections,
  useSelectionFieldOptions,
} from "../../hooks/useSelections";
import AddOPMModal from "../../components/AddOPMModal";
import AddPaintModal from "../../components/AddPaintModal";
import { formatDateET } from "../../utils/dateUtils";
import {
  handlePrintSelections,
  handleDownloadCSV,
} from "../../utils/selectionsExport";
import SelectionsPrintLayout from "../../components/SelectionsPrintLayout";
import "../../styles/SelectionsPrint.css";
import InfoModal from "../../components/InfoModal";
import {
  FaHome,
  FaClock,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaCheckCircle,
  FaPaintBrush,
  FaShoppingCart,
  FaFolderOpen,
  FaPlus,
  FaTrash,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaUser,
  FaFilePdf,
  FaFileAlt,
  FaExclamation,
  FaClipboardList,
  FaWarehouse,
  FaDoorOpen,
  FaStream,
  FaBath,
  FaTimes,
  FaDownload,
  FaPrint,
  FaFileDownload,
  FaPencilAlt,
  FaSave,
  FaCheck,
  FaImage,
  FaEye,
  FaStickyNote,
  FaQuestionCircle,
  FaEllipsisH,
} from "react-icons/fa";
import { FiDroplet, FiHash } from "react-icons/fi";
import "./ClientSelections.css";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Tab configuration for selections page
 * @constant {Array<Object>}
 */
const SELECTION_TABS = [
  { id: "overview", label: "Overview", icon: FaClipboardList },
  { id: "paint", label: "Paint Selections", icon: FaPaintBrush },
  { id: "purchases", label: "Owner Purchases", icon: FaShoppingCart },
  { id: "documents", label: "Documents", icon: FaFolderOpen },
];

/**
 * Document categories matching selectionsService mapping
 * @constant {Array<Object>}
 */
const DOCUMENT_CATEGORIES = [
  { key: "roofGutter", label: "Gutters" },
  { key: "doorsWindows", label: "Doors & Windows" },
  { key: "garageDoor", label: "Garage Door" },
  { key: "fireplace", label: "Fireplace" },
  { key: "cabinets", label: "Cabinets" },
  { key: "countertopsSinks", label: "Countertops & Sinks" },
  { key: "showersTubs", label: "Shower Glass" },
  { key: "plumbingFixtures", label: "Plumbing Fixtures" },
  { key: "lightingFixtures", label: "Lighting Fixtures" },
  { key: "flooring", label: "Flooring" },
  { key: "tile", label: "Tile" },
  { key: "finalSelectionsPDF", label: "Final Selections PDF" },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate days remaining until deadline in Eastern Time
 * @param {string} dueDate - ISO date string
 * @returns {number|null} Days remaining or null if no date
 */
const calculateDaysRemaining = (dueDate) => {
  if (!dueDate) return null;

  try {
    const todayET = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
    );
    todayET.setHours(0, 0, 0, 0);

    const targetString = dueDate.includes("T")
      ? dueDate
      : `${dueDate}T12:00:00`;
    const targetDate = new Date(targetString);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate - todayET;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error("Error calculating days remaining:", error);
    return null;
  }
};

/**
 * Derive a short display label for a document's file type
 * Prefers the filename extension, falls back to the MIME subtype
 * @param {Object} doc - Attachment object ({ filename, name, type })
 * @returns {string} Short uppercase label, e.g. "PDF", "JPG"
 */
const getFileTypeLabel = (doc) => {
  const filename = doc?.filename || doc?.name || "";
  const ext = filename.includes(".") ? filename.split(".").pop() : "";
  if (ext && ext.length <= 5) return ext.toUpperCase();

  const subtype = doc?.type?.split("/")[1];
  return subtype ? subtype.toUpperCase() : "File";
};

// ============================================================================
// SHARED SUB-COMPONENTS
// ============================================================================

/**
 * Attribution badge shown on a field/row last touched by a team member.
 * `adminEdit` is the attribution entry ({ name, at, action? }) or
 * null/undefined when the value was not last set by an admin.
 *
 * Paint / owner-purchase rows carry an `action` ("added" or "updated");
 * overview fields have none and fall back to "Set by admin".
 * `verbOverride` forces a specific verb regardless of `action` (e.g.
 * "Updated" for the free-text notes field, only ever edited).
 *
 * @param {Object} props
 * @param {{name: string, at: string, action?: string}|null}
 *   [props.adminEdit] - Attribution entry
 * @param {string} [props.verbOverride] - Force the badge verb
 */
const AdminBadge = ({ adminEdit, verbOverride }) => {
  if (!adminEdit) return null;

  const VERBS = { added: "Added", updated: "Updated" };
  const verb = verbOverride || VERBS[adminEdit.action] || "Set";

  const who = adminEdit.name || "an admin";
  const when = adminEdit.at ? ` on ${formatDateET(adminEdit.at)}` : "";

  return (
    <span className="cs-admin-badge" data-tooltip={`${verb} by ${who}${when}`}>
      {verb} by admin
    </span>
  );
};

/**
 * Row overflow ("kebab") menu for paint / owner-purchase cards.
 * Renders a horizontal-ellipsis trigger that opens a small action menu.
 * When `disabled` is set the trigger is inert and the menu can't open
 * (e.g. paint cards after the selections deadline).
 *
 * @param {Object} props
 * @param {Function} props.onEdit - Handler for the Edit action
 * @param {Function} [props.onDelete] - Handler for the Remove action;
 *   omit to render an edit-only menu
 * @param {string} [props.editLabel="Edit"] - Edit action label
 * @param {string} [props.deleteLabel="Remove"] - Remove action label
 * @param {boolean} [props.disabled=false] - Disable the whole menu
 * @param {string} [props.disabledTooltip] - Tooltip when disabled
 */
const RowActionsMenu = ({
  onEdit,
  onDelete,
  editLabel = "Edit",
  deleteLabel = "Remove",
  disabled = false,
  disabledTooltip,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="cs-row-actions">
      <button
        type="button"
        className="cs-row-actions-trigger"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-tooltip={disabled ? disabledTooltip : "Actions"}
      >
        <FaEllipsisH />
      </button>
      {isOpen && !disabled && (
        <>
          <div
            className="cs-row-actions-backdrop"
            onClick={() => setIsOpen(false)}
          />
          <div className="cs-row-actions-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                onEdit();
              }}
            >
              <FaPencilAlt /> {editLabel}
            </button>
            {onDelete && (
              <button
                type="button"
                role="menuitem"
                className="cs-row-actions-danger"
                onClick={() => {
                  setIsOpen(false);
                  onDelete();
                }}
              >
                <FaTrash /> {deleteLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Selection field display component
 * Shows value with check icon or "Selection Needed" with warning icon
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {*} props.value - Field value (string, boolean, or array)
 * @param {boolean} [props.isBoolean=false] - Whether field is boolean type
 * @param {Object} [props.adminEdit] - Admin attribution entry, if any
 */
const SelectionField = ({
  label,
  value,
  isBoolean = false,
  overrideDisplay = undefined,
  adminEdit = null,
}) => {
  let displayValue;
  let hasVal = false;

  if (overrideDisplay !== undefined) {
    // Force a complete state with custom text (e.g. selection made elsewhere)
    hasVal = true;
    displayValue = overrideDisplay;
  } else if (isBoolean) {
    hasVal = value === true;
    displayValue = hasVal ? "Yes" : null;
  } else if (Array.isArray(value)) {
    hasVal = value.length > 0;
    displayValue = hasVal ? value.join(", ") : null;
  } else {
    hasVal = !!value && value.trim() !== "";
    displayValue = hasVal ? value : null;
  }

  return (
    <div className="cs-selection-field">
      <div className="cs-field-header">
        {hasVal ? (
          <FaCheckCircle className="cs-field-icon cs-field-icon--complete" />
        ) : (
          <FaExclamationCircle className="cs-field-icon cs-field-icon--pending" />
        )}
        <span className="cs-field-label">{label}</span>
        <AdminBadge adminEdit={adminEdit} />
      </div>
      <span
        className={`cs-field-value ${!hasVal ? "cs-field-value--needed" : ""}`}
      >
        {displayValue || "Selection Needed"}
      </span>
    </div>
  );
};

/**
 * Vendor ordering field component
 * Shows ordering method or "Selection Needed"
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Ordering method value
 * @param {Object} [props.adminEdit] - Admin attribution entry, if any
 */
const VendorField = ({ label, value, adminEdit = null }) => {
  const hasVal = !!value && value.trim() !== "";

  return (
    <div className="cs-selection-field">
      <div className="cs-field-header">
        {hasVal ? (
          <FaCheckCircle className="cs-field-icon cs-field-icon--complete" />
        ) : (
          <FaExclamationCircle className="cs-field-icon cs-field-icon--pending" />
        )}
        <span className="cs-field-label">{label}</span>
        <AdminBadge adminEdit={adminEdit} />
      </div>
      <span
        className={`cs-field-value ${!hasVal ? "cs-field-value--needed" : ""}`}
      >
        {hasVal ? value : "Selection Needed"}
      </span>
    </div>
  );
};

/**
 * Tab navigation component
 * @param {Object} props
 * @param {Array} props.tabs - Tab configuration array
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onTabChange - Tab change handler
 */
const TabNav = ({ tabs, activeTab, onTabChange }) => (
  <div className="cs-tabs-nav">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        className={`cs-tab-btn ${activeTab === tab.id ? "cs-tab-btn--active" : ""}`}
        onClick={() => onTabChange(tab.id)}
      >
        <tab.icon />
        <span>{tab.label}</span>
      </button>
    ))}
  </div>
);

// ============================================================================
// EDIT MODE FIELD COMPONENTS
// ============================================================================

/**
 * Text input field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for view-options eye icon
 */
const EditTextField = ({ label, value, onChange, infoAction }) => (
  <div className="cs-edit-field">
    <span className="cs-edit-field-label">
      {label}
      {infoAction && (
        <span data-tooltip="View options/vendors">
          <FaEye
            className="cs-field-info-eye"
            onClick={infoAction}
            aria-label="View options/vendors"
          />
        </span>
      )}
    </span>
    <input
      type="text"
      className="cs-edit-input"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/**
 * Auto-expanding long-text field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {number} [props.maxLength=5000] - Character limit
 */
const EditTextAreaField = ({ label, value, onChange, maxLength = 5000 }) => {
  const textareaRef = useRef(null);

  const resize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    resize(textareaRef.current);
  }, [value]);

  return (
    <div className="cs-edit-field cs-edit-field--full">
      <span className="cs-edit-field-label">{label}</span>
      <textarea
        ref={textareaRef}
        className="cs-edit-textarea"
        value={value || ""}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onInput={(e) => resize(e.target)}
      />
      <span className="cs-edit-char-counter">
        {(value || "").length}/{maxLength}
      </span>
    </div>
  );
};

/**
 * Select dropdown field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Array<string>} props.options - Dropdown options
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for view-options eye icon
 * @param {boolean} [props.disabled] - Disable the dropdown
 * @param {React.ReactNode} [props.helpMessage] - Optional helper text below the dropdown
 */
const EditSelectField = ({
  label,
  value,
  options,
  onChange,
  infoAction,
  disabled = false,
  helpMessage = null,
}) => (
  <div className="cs-edit-field">
    <span className="cs-edit-field-label">
      {label}
      {infoAction && (
        <span data-tooltip="View options/vendors">
          <FaEye
            className="cs-field-info-eye"
            onClick={infoAction}
            aria-label="View options/vendors"
          />
        </span>
      )}
    </span>
    <select
      className="cs-edit-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">-- Select --</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    {helpMessage && <p className="cs-edit-field-help">{helpMessage}</p>}
  </div>
);

/**
 * Checkbox toggle field for edit mode
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {boolean} props.checked - Current checked state
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.infoAction] - Optional click handler for view-options eye icon
 */
const EditCheckboxField = ({ label, checked, onChange, infoAction }) => (
  <div className="cs-edit-field">
    <div className="cs-edit-checkbox-row">
      <input
        type="checkbox"
        id={`edit-${label.replace(/\s/g, "-")}`}
        checked={checked || false}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label
        className="cs-edit-checkbox-label"
        htmlFor={`edit-${label.replace(/\s/g, "-")}`}
      >
        {label}
        {infoAction && (
          <span data-tooltip="View options/vendors">
            <FaEye
              className="cs-field-info-eye"
              onClick={(e) => {
                e.preventDefault();
                infoAction();
              }}
              aria-label="View options/vendors"
            />
          </span>
        )}
      </label>
    </div>
  </div>
);

/**
 * Multi-select checkboxes for edit mode (e.g. Siding Types)
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {Array<string>} props.selected - Currently selected values
 * @param {Array<string>} props.options - Available options
 * @param {Function} props.onChange - Change handler (receives full array)
 */
const EditMultiSelectField = ({ label, selected, options, onChange }) => {
  const selectedArray = Array.isArray(selected) ? selected : [];

  const handleToggle = (option) => {
    if (selectedArray.includes(option)) {
      onChange(selectedArray.filter((s) => s !== option));
    } else {
      onChange([...selectedArray, option]);
    }
  };

  return (
    <div className="cs-edit-field">
      <span className="cs-edit-field-label">{label}</span>
      <div className="cs-edit-multi-select">
        {options.map((opt) => (
          <div key={opt} className="cs-edit-multi-option">
            <input
              type="checkbox"
              id={`multi-${opt.replace(/\s/g, "-")}`}
              checked={selectedArray.includes(opt)}
              onChange={() => handleToggle(opt)}
            />
            <label htmlFor={`multi-${opt.replace(/\s/g, "-")}`}>{opt}</label>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Save/Cancel action bar for edit mode sections
 * @param {Object} props
 * @param {Function} props.onSave - Save handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isSaving - Whether save is in progress
 */
const EditActions = ({ onSave, onCancel, isSaving }) => (
  <div className="cs-edit-actions">
    <button
      className="cs-edit-cancel-btn"
      onClick={onCancel}
      disabled={isSaving}
    >
      <FaTimes /> Cancel
    </button>
    <button className="cs-edit-save-btn" onClick={onSave} disabled={isSaving}>
      {isSaving ? (
        <>Saving...</>
      ) : (
        <>
          <FaSave /> Save Changes
        </>
      )}
    </button>
  </div>
);

// ============================================================================
// OVERVIEW TAB - 4 SECTIONS WITH INLINE EDITING
// ============================================================================

/**
 * Exterior selections subsection with inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler to open view-options eye icon action
 */
const ExteriorSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;
  const sidingTypesArray = Array.isArray(data.sidingTypes)
    ? data.sidingTypes
    : [];
  const isMetalRoof = data.roofMaterial === "Metal";

  if (isEditing) {
    return (
      <div className="cs-fields-grid">
        <EditSelectField
          label="Roof Material"
          value={data.roofMaterial}
          options={fieldOptions?.roofMaterial || []}
          onChange={(val) => {
            onFieldChange("roofMaterial", val);
            // Metal roof color is chosen via the metal supplier, not this dropdown
            if (val === "Metal") onFieldChange("roofColor", "");
          }}
        />
        {isMetalRoof ? (
          <div className="cs-edit-field">
            <span className="cs-edit-field-label">
              Roof Material Color
              <span data-tooltip="View options/vendors">
                <FaEye
                  className="cs-field-info-eye"
                  onClick={() => onViewOptions("roofMetalSupplier")}
                  aria-label="View options/vendors"
                />
              </span>
            </span>
            <span className="cs-edit-field-help-container">
              <p className="cs-edit-field-help">
                Visit our{" "}
                <button
                  type="button"
                  className="cs-edit-field-help-link"
                  onClick={() => onViewOptions("roofMetalSupplier")}
                >
                  Metal Supplier
                </button>{" "}
                to make selection.
              </p>
            </span>
          </div>
        ) : (
          <EditSelectField
            label="Roof Material Color"
            value={data.roofColor}
            options={fieldOptions?.roofColor || []}
            onChange={(val) => onFieldChange("roofColor", val)}
            infoAction={() => onViewOptions("roofColor")}
          />
        )}
        <EditSelectField
          label="Gutter/Downspout Color"
          value={data.gutterColor}
          options={fieldOptions?.gutterColor || []}
          onChange={(val) => onFieldChange("gutterColor", val)}
          infoAction={() => onViewOptions("gutterColor")}
        />
        <EditSelectField
          label="Garage Door Chosen"
          value={data.garageDoorsChosen}
          options={fieldOptions?.garageDoorsChosen || []}
          onChange={(val) => onFieldChange("garageDoorsChosen", val)}
          infoAction={() => onViewOptions("garageDoorsChosen")}
        />
        <EditMultiSelectField
          label="Siding Type(s)"
          selected={data.sidingTypes}
          options={fieldOptions?.sidingTypes || []}
          onChange={(val) => onFieldChange("sidingTypes", val)}
        />
        {sidingTypesArray.includes("Fiber Cement Board") && (
          <EditSelectField
            label="Fiber Cement Board Option"
            value={data.fiberCementBoard}
            options={fieldOptions?.fiberCementBoard || []}
            onChange={(val) => onFieldChange("fiberCementBoard", val)}
            infoAction={() => onViewOptions("fiberCementBoard")}
          />
        )}
        {sidingTypesArray.includes("Brick") && (
          <EditCheckboxField
            label="Brick Chosen"
            checked={data.brickChosen}
            onChange={(val) => onFieldChange("brickChosen", val)}
            infoAction={() => onViewOptions("brickChosen")}
          />
        )}
        {sidingTypesArray.includes("Stone") && (
          <EditCheckboxField
            label="Stone Chosen"
            checked={data.stoneChosen}
            onChange={(val) => onFieldChange("stoneChosen", val)}
            infoAction={() => onViewOptions("stoneChosen")}
          />
        )}
        {sidingTypesArray.includes("Wood") && (
          <EditCheckboxField
            label="Wood Chosen"
            checked={data.woodChosen}
            onChange={(val) => onFieldChange("woodChosen", val)}
          />
        )}
        <EditCheckboxField
          label="Exterior Doors & Windows Chosen"
          checked={data.exteriorDoorsWindowsChosen}
          onChange={(val) => onFieldChange("exteriorDoorsWindowsChosen", val)}
          infoAction={() => onViewOptions("exteriorDoorsChosen")}
        />
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="cs-fields-grid">
      <SelectionField
        label="Roof Material"
        value={selections.roofMaterial}
        adminEdit={adminEdits.roofMaterial}
      />
      <SelectionField
        label="Roof Material Color"
        value={selections.roofColor}
        adminEdit={adminEdits.roofColor}
        overrideDisplay={
          selections.roofMaterial === "Metal"
            ? "Selected via Metal Supplier"
            : undefined
        }
      />
      <SelectionField
        label="Gutter/Downspout Color"
        value={selections.gutterColor}
        adminEdit={adminEdits.gutterColor}
      />
      <SelectionField
        label="Garage Door Chosen"
        value={selections.garageDoorsChosen}
        adminEdit={adminEdits.garageDoorsChosen}
      />
      <SelectionField
        label="Siding Type"
        value={sidingTypesArray.length > 0 ? sidingTypesArray.join(", ") : ""}
        adminEdit={adminEdits.sidingTypes}
      />
      {sidingTypesArray.includes("Fiber Cement Board") && (
        <SelectionField
          label="Fiber Cement Board Option"
          value={selections.fiberCementBoard}
          adminEdit={adminEdits.fiberCementBoard}
        />
      )}
      {sidingTypesArray.includes("Brick") && (
        <SelectionField
          label="Brick Chosen"
          value={selections.brickChosen}
          isBoolean={true}
          adminEdit={adminEdits.brickChosen}
        />
      )}
      {sidingTypesArray.includes("Stone") && (
        <SelectionField
          label="Stone Chosen"
          value={selections.stoneChosen}
          isBoolean={true}
          adminEdit={adminEdits.stoneChosen}
        />
      )}
      {sidingTypesArray.includes("Wood") && (
        <SelectionField
          label="Wood Chosen"
          value={selections.woodChosen}
          isBoolean={true}
          adminEdit={adminEdits.woodChosen}
        />
      )}
      <SelectionField
        label="Exterior Doors & Windows Chosen"
        value={selections.exteriorDoorsWindowsChosen}
        isBoolean={true}
        adminEdit={adminEdits.exteriorDoorsWindowsChosen}
      />
    </div>
  );
};

/**
 * Interior selections subsection with inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler to open view-options eye icon action
 */
const InteriorSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  if (isEditing) {
    return (
      <div className="cs-fields-grid">
        <EditSelectField
          label="Door Pattern"
          value={data.interiorDoorPattern}
          options={fieldOptions?.interiorDoorPattern || []}
          onChange={(val) => onFieldChange("interiorDoorPattern", val)}
          infoAction={() => onViewOptions("interiorDoorPattern")}
        />
        <EditSelectField
          label="Core Type"
          value={data.interiorDoorCore}
          options={fieldOptions?.interiorDoorCore || []}
          onChange={(val) => onFieldChange("interiorDoorCore", val)}
        />
        <EditTextField
          label="Hinge Finish"
          value={data.hingeFinish}
          onChange={(val) => onFieldChange("hingeFinish", val)}
        />
        <EditCheckboxField
          label="Door Knobs Purchased"
          checked={data.doorKnobsPurchased}
          onChange={(val) => onFieldChange("doorKnobsPurchased", val)}
        />
        <EditSelectField
          label="Flooring Direction"
          value={data.flooringDirection}
          options={fieldOptions?.flooringDirection || []}
          onChange={(val) => onFieldChange("flooringDirection", val)}
          infoAction={() => onViewOptions("flooringDirection")}
        />
        <EditSelectField
          label="Painted Closet Shelves"
          value={data.closetShelving}
          options={fieldOptions?.closetShelving || []}
          onChange={(val) => onFieldChange("closetShelving", val)}
          infoAction={() => onViewOptions("closetShelving")}
        />
        <EditSelectField
          label="Switch Plate Color"
          value={data.switchPlateColor}
          options={fieldOptions?.switchPlateColor || []}
          onChange={(val) => onFieldChange("switchPlateColor", val)}
        />
        <EditTextField
          label="Vent Color"
          value={data.ventStyle}
          onChange={(val) => onFieldChange("ventStyle", val)}
        />
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="cs-fields-grid">
      <SelectionField
        label="Door Pattern"
        value={selections.interiorDoorPattern}
        adminEdit={adminEdits.interiorDoorPattern}
      />
      <SelectionField
        label="Core Type"
        value={selections.interiorDoorCore}
        adminEdit={adminEdits.interiorDoorCore}
      />
      <SelectionField
        label="Hinge Finish"
        value={selections.hingeFinish}
        adminEdit={adminEdits.hingeFinish}
      />
      <SelectionField
        label="Door Knobs Purchased"
        value={selections.doorKnobsPurchased}
        isBoolean={true}
        adminEdit={adminEdits.doorKnobsPurchased}
      />
      <SelectionField
        label="Flooring Direction"
        value={selections.flooringDirection}
        adminEdit={adminEdits.flooringDirection}
      />
      <SelectionField
        label="Painted Closet Shelves"
        value={selections.closetShelving}
        adminEdit={adminEdits.closetShelving}
      />
      <SelectionField
        label="Switch Plate Color"
        value={selections.switchPlateColor}
        adminEdit={adminEdits.switchPlateColor}
      />
      <SelectionField
        label="Vent Color"
        value={selections.ventStyle}
        adminEdit={adminEdits.ventStyle}
      />
    </div>
  );
};

/**
 * Trim selections subsection with inline editing
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for view-options (unused in Trim)
 */
const TrimSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  if (isEditing) {
    // When "Other" is chosen, point the client to the
    // Additional Selections / Notes section to drop a reference link.
    const casingStyleIsOther =
      typeof data.casingStyle === "string" &&
      data.casingStyle.trim().toLowerCase().startsWith("other");

    const scrollToNotes = () =>
      document
        .getElementById("cs-section-additionalNotes")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

    const casingStyleHelp = casingStyleIsOther ? (
      <span className="cs-edit-field-help-container">
        <p className="cs-edit-field-help">
          Please add a reference link for your selection in the{" "}
          <button
            type="button"
            className="cs-edit-field-help-link"
            onClick={scrollToNotes}
          >
            Additional Selections / Notes
          </button>{" "}
          section below.
        </p>
      </span>
    ) : null;

    return (
      <div className="cs-fields-grid">
        <EditSelectField
          label="Door & Window Trim Style"
          value={data.casingStyle}
          options={fieldOptions?.casingStyle || []}
          onChange={(val) => onFieldChange("casingStyle", val)}
          infoAction={() => onViewOptions("casingStyle")}
          helpMessage={casingStyleHelp}
        />
        <EditSelectField
          label="Trim Detail"
          value={data.trimDetail}
          options={fieldOptions?.trimDetail || []}
          onChange={(val) => onFieldChange("trimDetail", val)}
          infoAction={() => onViewOptions("trimDetail")}
        />
        <EditSelectField
          label="Baseboard Height"
          value={data.baseboardHeight}
          options={fieldOptions?.baseboardHeight || []}
          onChange={(val) => onFieldChange("baseboardHeight", val)}
        />
        <EditSelectField
          label="Crown Moulding Size"
          value={data.crownMouldingSize}
          options={fieldOptions?.crownMouldingSize || []}
          onChange={(val) => onFieldChange("crownMouldingSize", val)}
        />
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="cs-fields-grid">
      <SelectionField
        label="Door & Window Trim Style"
        value={selections.casingStyle}
        adminEdit={adminEdits.casingStyle}
      />
      <SelectionField
        label="Trim Detail"
        value={selections.trimDetail}
        adminEdit={adminEdits.trimDetail}
      />
      <SelectionField
        label="Baseboard Height"
        value={selections.baseboardHeight}
        adminEdit={adminEdits.baseboardHeight}
      />
      <SelectionField
        label="Crown Moulding Size"
        value={selections.crownMouldingSize}
        adminEdit={adminEdits.crownMouldingSize}
      />
    </div>
  );
};

/**
 * Interior Features subsection with inline editing
 * Uses smart fallback: renders dropdown if options exist, text input if not
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {Function} props.onViewOptions - Handler for view-options (unused in Features)
 */
const InteriorFeaturesSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
  fieldOptions,
  onViewOptions,
}) => {
  const data = isEditing ? editData : selections;

  /**
   * Render a select if options exist for this field, otherwise text input.
   * Includes eye icon if the field has a view-options config.
   * @param {string} label - Display label
   * @param {string} fieldKey - Frontend field key
   */
  const renderVendorEditField = (label, fieldKey) => {
    const options = fieldOptions?.[fieldKey];
    const eyeAction = onViewOptions ? () => onViewOptions(fieldKey) : undefined;

    if (options && options.length > 0) {
      return (
        <EditSelectField
          label={label}
          value={data[fieldKey]}
          options={options}
          onChange={(val) => onFieldChange(fieldKey, val)}
          infoAction={eyeAction}
        />
      );
    }
    return (
      <EditTextField
        label={label}
        value={data[fieldKey]}
        onChange={(val) => onFieldChange(fieldKey, val)}
        infoAction={eyeAction}
      />
    );
  };

  if (isEditing) {
    return (
      <div className="cs-fields-grid">
        {renderVendorEditField("Cabinets Chosen", "cabinetsOrdered")}
        {renderVendorEditField("Counters/Sinks Chosen", "countersSinksOrdered")}
        {renderVendorEditField("Showers/Tubs Chosen", "showersTubsOrdered")}
        {renderVendorEditField("Shower Doors Chosen", "showerDoorsOrdered")}
        {renderVendorEditField("Fireplace Chosen", "fireplaceOrdered")}
        {renderVendorEditField("Floors Chosen", "floorsOrdered")}
        {renderVendorEditField("Tile Chosen", "tileOrdered")}
        {renderVendorEditField(
          "Plumbing Fixtures Chosen",
          "plumbingFixturesOrdered",
        )}
        {renderVendorEditField(
          "Lighting Fixtures Chosen",
          "lightingFixturesOrdered",
        )}
      </div>
    );
  }

  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="cs-fields-grid">
      <VendorField
        label="Cabinets Chosen"
        value={selections.cabinetsOrdered}
        adminEdit={adminEdits.cabinetsOrdered}
      />
      <VendorField
        label="Counters/Sinks Chosen"
        value={selections.countersSinksOrdered}
        adminEdit={adminEdits.countersSinksOrdered}
      />
      <VendorField
        label="Showers/Tubs Chosen"
        value={selections.showersTubsOrdered}
        adminEdit={adminEdits.showersTubsOrdered}
      />
      <VendorField
        label="Shower Doors Chosen"
        value={selections.showerDoorsOrdered}
        adminEdit={adminEdits.showerDoorsOrdered}
      />
      <VendorField
        label="Fireplace Chosen"
        value={selections.fireplaceOrdered}
        adminEdit={adminEdits.fireplaceOrdered}
      />
      <VendorField
        label="Floors Chosen"
        value={selections.floorsOrdered}
        adminEdit={adminEdits.floorsOrdered}
      />
      <VendorField
        label="Tile Chosen"
        value={selections.tileOrdered}
        adminEdit={adminEdits.tileOrdered}
      />
      <VendorField
        label="Plumbing Fixtures Chosen"
        value={selections.plumbingFixturesOrdered}
        adminEdit={adminEdits.plumbingFixturesOrdered}
      />
      <VendorField
        label="Lighting Fixtures Chosen"
        value={selections.lightingFixturesOrdered}
        adminEdit={adminEdits.lightingFixturesOrdered}
      />
    </div>
  );
};

/**
 * Additional selections / notes subsection with inline editing
 * Freeform long-text for selections not covered by the structured fields
 * @param {Object} props
 * @param {Object} props.selections - Client selections data
 * @param {boolean} props.isEditing - Whether section is in edit mode
 * @param {Object} props.editData - Current edit state data
 * @param {Function} props.onFieldChange - Handler for field value changes
 */
const AdditionalNotesSection = ({
  selections,
  isEditing,
  editData,
  onFieldChange,
}) => {
  if (isEditing) {
    return (
      <div className="cs-fields-grid">
        <EditTextAreaField
          label="Selections Not Covered Above or Notes On Above Selections"
          value={editData.additionalNotes}
          onChange={(val) => onFieldChange("additionalNotes", val)}
        />
      </div>
    );
  }

  const notes = (selections.additionalNotes || "").trim();
  const hasNotes = notes !== "";
  const adminEdits = selections.adminEditedFields || {};

  return (
    <div className="cs-selection-field cs-notes-readonly">
      <div className="cs-field-header">
        {hasNotes ? (
          <FaCheckCircle className="cs-field-icon cs-field-icon--complete" />
        ) : (
          <FaQuestionCircle className="cs-field-icon cs-field-icon--empty" />
        )}
        <span className="cs-field-label">
          Selections Not Covered Above or Notes On Above Selections
        </span>
        <AdminBadge
          adminEdit={adminEdits.additionalNotes}
          verbOverride="Updated"
        />
      </div>
      {hasNotes ? (
        <p className="cs-notes-value">{selections.additionalNotes}</p>
      ) : (
        <p className="cs-notes-empty">No notes yet</p>
      )}
    </div>
  );
};

/**
 * Section configuration for the overview tab
 * Maps section IDs to their display components and metadata
 * @constant {Array<Object>}
 */
const OVERVIEW_SECTIONS = [
  {
    id: "exterior",
    label: "Exterior",
    icon: FaWarehouse,
    Component: ExteriorSection,
  },
  {
    id: "interior",
    label: "Interior",
    icon: FaDoorOpen,
    Component: InteriorSection,
  },
  {
    id: "trim",
    label: "Interior Trim",
    icon: FaStream,
    Component: TrimSection,
  },
  {
    id: "interiorFeatures",
    label: "Interior Features",
    icon: FaBath,
    Component: InteriorFeaturesSection,
  },
  {
    id: "additionalNotes",
    label: "Additional Selections / Notes",
    icon: FaStickyNote,
    Component: AdditionalNotesSection,
  },
];

/**
 * Overview tab content with 4 editable subsections
 * Manages per-section edit state, local edit data, and save/cancel
 * @param {Object} props
 * @param {Object} props.data - Client selections data object
 * @param {string} props.recordId - Airtable record ID for the selections row
 * @param {string} props.projectId - Airtable project record ID (for attribution)
 * @param {Object} props.updateMutation - useUpdateClientSelections mutation
 * @param {Object} props.fieldOptions - Dynamic dropdown options from Airtable
 * @param {boolean} props.isPastDeadline - Flag indicating if the deadline has passed
 */
const OverviewTab = ({
  data,
  recordId,
  projectId,
  updateMutation,
  fieldOptions,
  isPastDeadline,
}) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState(null);
  const [viewOptionsModal, setViewOptionsModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });

  const openInfoModal = (messageKey) => {
    setInfoModalMessage(messageKey);
    setIsInfoModalOpen(true);
  };

  // ========================================================================
  // FIELD VIEW OPTIONS CONFIG
  // Maps field keys to external links or modal content (images/links)
  // ========================================================================

  /**
   * Configuration for field "view options" eye icons.
   * Each entry defines either a direct external link or modal content
   * (supplier links, reference images) shown when the FaEye icon is clicked.
   * @constant {Object.<string, Object>}
   */
  const FIELD_VIEW_OPTIONS = {
    roofColor: {
      type: "modal",
      title: "Roof Color Options",
      /** @param {Object} currentData - Current edit data for conditional logic */
      getContent: (currentData) => {
        const isShingle = currentData?.roofMaterial === "Shingle";
        const isMetal = currentData?.roofMaterial === "Metal";
        const links = [];
        if (isShingle || (!isShingle && !isMetal)) {
          links.push({
            label: "View Shingle Colors (CertainTeed)",
            url: "https://www.certainteed.com/products/residential-roofing-products/landmark",
          });
        }
        if (isMetal || (!isShingle && !isMetal)) {
          links.push({
            label: "View Metal Options (B&M Metals)",
            url: "https://bmmetalroofing.com/",
          });
        }
        return { links };
      },
    },
    gutterColor: {
      type: "link",
      url: "https://senox.com/colors/",
    },
    fiberCementBoard: {
      type: "modal",
      title: "Fiber Cement Board Options",
      getContent: () => ({
        images: [
          { src: "/hardie-plank-smooth.webp", label: "Smooth" },
          { src: "/hardie-plank-textured.webp", label: "Textured" },
        ],
      }),
    },
    interiorDoorPattern: {
      type: "modal",
      title: "Door Patterns",
      getContent: () => ({
        images: [
          {
            src: "/New Door Patterns_2026.png",
            label: "Door Pattern Options",
          },
        ],
      }),
    },
    flooringDirection: {
      type: "modal",
      title: "Flooring Direction",
      getContent: () => ({
        images: [
          {
            src: "/parallel-floors.jpg",
            label: "Parallel to Main Door",
          },
          {
            src: "/perpendicular-floors.jpg",
            label: "Perpendicular to Main Door",
          },
        ],
      }),
    },
    closetShelving: {
      type: "modal",
      title: "Closet Shelves Options",
      getContent: () => ({
        images: [
          { src: "/closet.png", label: "Unpainted Shelves" },
          { src: "/painted shelves.png", label: "Painted Shelves" },
        ],
      }),
    },
    casingStyle: {
      type: "modal",
      title: "Casing Style Options",
      getContent: () => ({
        images: [{ src: "/trim_styles.png", label: "Casing Style Options" }],
      }),
    },
    trimDetail: {
      type: "modal",
      title: "Trim Detail Options",
      getContent: () => ({
        images: [{ src: "/moulding.png", label: "Trim Detail Options" }],
      }),
    },

    // Interior Features — scroll to matching vendor card
    cabinetsOrdered: { type: "scrollToVendor", vendorCategory: "Cabinet" },
    countersSinksOrdered: {
      type: "scrollToVendor",
      vendorCategory: "Countertop",
    },
    showersTubsOrdered: { type: "scrollToVendor", vendorCategory: "Plumbing" },
    showerDoorsOrdered: {
      type: "scrollToVendor",
      vendorCategory: "Glass/Mirror",
    },
    fireplaceOrdered: { type: "scrollToVendor", vendorCategory: "Fireplace" },
    floorsOrdered: { type: "scrollToVendor", vendorCategory: "Flooring" },
    tileOrdered: { type: "scrollToVendor", vendorCategory: "Tile / Masonry" },
    plumbingFixturesOrdered: {
      type: "scrollToVendor",
      vendorCategory: "Plumbing",
    },
    lightingFixturesOrdered: {
      type: "scrollToVendor",
      vendorCategory: "Lighting",
    },

    // Exterior — scroll to matching vendor card
    roofMetalSupplier: { type: "scrollToVendor", vendorCategory: "Metal" },
    brickChosen: { type: "scrollToVendor", vendorCategory: "Brick" },
    stoneChosen: { type: "scrollToVendor", vendorCategory: "Stone" },
    exteriorDoorsChosen: {
      type: "scrollToVendor",
      vendorCategory: "Window & Door",
    },
    garageDoorsChosen: {
      type: "scrollToVendor",
      vendorCategory: "Garage Door",
    },
  };

  /**
   * Handle eye icon click for a field's view options.
   * Opens an external link, shows a modal with images/links,
   * or scrolls to and highlights the matching vendor card.
   * @param {string} fieldKey - Key matching FIELD_VIEW_OPTIONS config
   */
  const handleViewOptions = (fieldKey) => {
    const config = FIELD_VIEW_OPTIONS[fieldKey];
    if (!config) return;

    if (config.type === "link") {
      window.open(config.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (config.type === "modal") {
      const content =
        typeof config.getContent === "function"
          ? config.getContent(editData)
          : { text: config.text };
      setViewOptionsModal({
        isOpen: true,
        title: config.title,
        content,
      });
      return;
    }

    if (config.type === "scrollToVendor") {
      const vendorCards = document.querySelectorAll(".cs-vendor-card");
      for (const card of vendorCards) {
        const categories = card.getAttribute("data-vendor-categories") || "";
        if (categories.split(",").includes(config.vendorCategory)) {
          card.scrollIntoView({ behavior: "smooth", block: "center" });
          card.classList.add("cs-vendor-card--highlight");
          setTimeout(() => {
            card.classList.remove("cs-vendor-card--highlight");
          }, 2000);
          return;
        }
      }
    }
  };

  if (!data) {
    return (
      <div className="cs-empty-state">
        <FaClipboardList />
        <p>No selections data available.</p>
      </div>
    );
  }

  /**
   * Enter edit mode for a specific section
   * Copies current data into local edit state
   * @param {string} sectionId - Section identifier
   */
  const handleStartEdit = (sectionId) => {
    setEditData({ ...data });
    setEditingSection(sectionId);
    setSaveSuccess(null);
    setSaveError(null);
  };

  /**
   * Whether the current edit session has unsaved changes
   * @return {boolean}
   */
  const hasUnsavedChanges = () => {
    const skip = [
      "id",
      "projectName",
      "documents",
      "selectionsDueDate",
      "selectionRecordId",
    ];
    return Object.entries(editData).some(([key, value]) => {
      if (skip.includes(key)) return false;
      const original = data[key];
      return Array.isArray(value)
        ? JSON.stringify(value) !== JSON.stringify(original)
        : value !== original;
    });
  };

  /**
   * Cancel editing and reset local state
   * Confirms before discarding if there are unsaved changes
   */
  const handleCancelEdit = () => {
    if (
      hasUnsavedChanges() &&
      // eslint-disable-next-line no-alert
      !window.confirm("You have unsaved changes. Discard them?")
    ) {
      return;
    }
    setEditingSection(null);
    setEditData({});
    setSaveError(null);
  };

  /**
   * Update a single field in local edit state
   * @param {string} field - Field key (camelCase)
   * @param {*} value - New value
   */
  const handleFieldChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Save changes for the currently editing section
   * Only sends fields that actually changed
   */
  const handleSave = async () => {
    if (!recordId || !editingSection) return;

    // Build diff - only send changed fields
    const changedFields = {};
    for (const [key, value] of Object.entries(editData)) {
      // Skip non-editable/metadata fields
      if (
        key === "id" ||
        key === "projectName" ||
        key === "documents" ||
        key === "selectionsDueDate" ||
        key === "selectionRecordId"
      ) {
        continue;
      }

      const original = data[key];
      // Compare arrays by stringifying
      if (Array.isArray(value)) {
        if (JSON.stringify(value) !== JSON.stringify(original)) {
          changedFields[key] = value;
        }
      } else if (value !== original) {
        changedFields[key] = value;
      }
    }

    if (Object.keys(changedFields).length === 0) {
      // Nothing changed - just exit edit mode
      setEditingSection(null);
      setEditData({});
      return;
    }

    try {
      setSaveError(null);
      await updateMutation.mutateAsync({
        recordId,
        projectId,
        updates: changedFields,
      });
      setSaveSuccess(editingSection);
      setEditingSection(null);
      setEditData({});

      // Clear success toast after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError("Failed to save changes. Please try again.");
    }
  };

  return (
    <div className="cs-active-container">
      <div className="cs-section-header-row">
        <h3 className="cs-section-header">Selections Overview</h3>
        <button
          className="cs-btn-add"
          onClick={() => openInfoModal("selectionsInstructions")}
        >
          <FaExclamationCircle /> Instructions
        </button>
      </div>
      <div className="cs-overview-alert">
        <FaExclamationCircle />
        <span>
          If you need to make a selection that isn't covered by the fields below
          or need to provide additional information about a selection, please
          use the{" "}
          <span
            className="cs-inline-link"
            role="button"
            tabIndex={0}
            onClick={() =>
              document
                .getElementById("cs-section-additionalNotes")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById("cs-section-additionalNotes")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            Additional Selections / Notes
          </span>{" "}
          section or reach out to your project manager.
        </span>
      </div>
      {saveSuccess && (
        <div className="cs-save-toast">
          <FaCheck /> Selections saved successfully
        </div>
      )}
      {saveError && (
        <div className="cs-save-toast cs-save-error">
          <FaExclamationCircle /> {saveError}
        </div>
      )}

      {OVERVIEW_SECTIONS.map(({ id, label, icon: Icon, Component }) => {
        const isSectionEditing = editingSection === id;
        const isNotesSection = id === "additionalNotes";
        // Additional Selections / Notes stays editable after the
        // deadline — it's the client's escape hatch for anything not
        // covered by the structured fields.
        const sectionLocked = isPastDeadline && !isNotesSection;
        const editLabel = isNotesSection ? "Edit Notes" : "Edit Selections";

        return (
          <div key={id} id={`cs-section-${id}`} className="cs-overview-section">
            <div className="cs-overview-section-header">
              <h3 className="cs-overview-section-title">
                <Icon /> {label}
              </h3>
              {!editingSection && (
                <span
                  data-tooltip={
                    sectionLocked
                      ? "Selections deadline has passed"
                      : "Make selections"
                  }
                >
                  <button
                    className="cs-edit-toggle-btn"
                    onClick={() => handleStartEdit(id)}
                    disabled={sectionLocked}
                    aria-label={
                      sectionLocked
                        ? "Selections deadline has passed"
                        : "Make selections"
                    }
                  >
                    <FaPencilAlt /> {editLabel}
                  </button>
                </span>
              )}
              {isSectionEditing && (
                <button className="cs-edit-toggle-btn" disabled={true}>
                  Editing...
                </button>
              )}
            </div>

            <Component
              selections={data}
              isEditing={isSectionEditing}
              editData={editData}
              onFieldChange={handleFieldChange}
              fieldOptions={fieldOptions}
              onViewOptions={handleViewOptions}
            />

            {isSectionEditing && (
              <EditActions
                onSave={handleSave}
                onCancel={handleCancelEdit}
                isSaving={updateMutation.isPending}
              />
            )}
          </div>
        );
      })}
      {/* Info Modal */}
      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Selections Instructions"
        message={infoModalMessage}
      />

      {/* View Options Modal (images/links for field eye icons) */}
      {viewOptionsModal.isOpen && (
        <InfoModal
          isOpen={viewOptionsModal.isOpen}
          onClose={() =>
            setViewOptionsModal({ isOpen: false, title: "", content: null })
          }
          title={viewOptionsModal.title}
        >
          <div className="cs-view-options-content">
            {viewOptionsModal.content?.text && (
              <p className="cs-view-option-message">
                {viewOptionsModal.content.text}
              </p>
            )}
            {viewOptionsModal.content?.links?.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cs-view-option-link"
              >
                <FaExternalLinkAlt /> {link.label}
              </a>
            ))}
            {viewOptionsModal.content?.images?.map((img, i) => (
              <div key={i} className="cs-view-option-image">
                <p className="cs-view-option-image-label">{img.label}</p>
                <span data-tooltip="Click to view full size">
                  <img
                    src={img.src}
                    alt={img.label}
                    onClick={() =>
                      window.open(img.src, "_blank", "noopener,noreferrer")
                    }
                  />
                </span>
              </div>
            ))}
          </div>
        </InfoModal>
      )}
    </div>
  );
};

// ============================================================================
// PAINT TAB
// ============================================================================

/**
 * Paint selections tab with add/edit/remove capability
 * @param {Object} props
 * @param {Array} props.paintSelections - Array of paint selection objects
 * @param {Function} props.onAdd - Handler to open add paint modal
 * @param {Function} props.onEdit - Handler to open edit paint modal
 * @param {Function} props.onDelete - Handler to confirm paint deletion
 * @param {boolean} props.isPastDeadline - Flag indicating if the deadline has passed
 */
const PaintTab = ({
  paintSelections,
  onAdd,
  onEdit,
  onDelete,
  isPastDeadline,
}) => (
  <div className="cs-active-container">
    <div className="cs-section-header-row">
      <h3 className="cs-section-header">Paint Selections</h3>
      <span
        data-tooltip={isPastDeadline ? "Selections deadline has passed" : null}
      >
        <button
          className="cs-btn-add"
          onClick={onAdd}
          disabled={isPastDeadline}
          aria-label={
            isPastDeadline ? "Selections deadline has passed" : "Add Paint"
          }
        >
          <FaPlus /> Add Paint
        </button>
      </span>
    </div>
    {paintSelections.length > 0 ? (
      <div className="cs-cards-grid">
        {paintSelections.map((paint) => {
          const paintName =
            paint.paintSelection || paint.paintCode || "Paint Selection";
          const swatchUrl = paint.swColorSwatch;
          return (
            <div key={paint.id} className="cs-paint-card">
              <div
                className="cs-paint-swatch-banner"
                style={
                  swatchUrl
                    ? {
                        backgroundImage: `url(${swatchUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : { backgroundColor: "var(--color-surface)" }
                }
              >
                <div className="cs-card-actions-corner">
                  <RowActionsMenu
                    onEdit={() => onEdit(paint)}
                    onDelete={() => onDelete({ type: "paint", id: paint.id })}
                    editLabel="Edit Paint Selection"
                    deleteLabel="Remove Paint Selection"
                    disabled={isPastDeadline}
                    disabledTooltip="Selections deadline has passed"
                  />
                </div>
                <span className="cs-paint-swatch-name">{paintName}</span>
                {paint.isUpgrade ? (
                  <span className="cs-paint-upgrade-badge">Upgrade</span>
                ) : (
                  <span />
                )}
              </div>
              <div className="cs-paint-card-body">
                <div className="cs-paint-options">
                  <div className="cs-paint-row">
                    <FiDroplet className="cs-paint-row-icon" />
                    <span className="cs-paint-row-label">Sheen:</span>
                    <span className="cs-paint-row-value">
                      {paint.sheen || "—"}
                    </span>
                  </div>
                  <div className="cs-paint-row">
                    <FiHash className="cs-paint-row-icon" />
                    <span className="cs-paint-row-label">Texture:</span>
                    <span className="cs-paint-row-value">
                      {paint.texture || "—"}
                    </span>
                  </div>
                </div>
                <div className="cs-paint-divider" />
                <span className="cs-paint-row-label">Areas</span>
                {paint.areas?.length > 0 && (
                  <div className="cs-paint-chips">
                    {paint.areas.map((area, i) => (
                      <span key={i} className="cs-paint-chip">
                        {area}
                      </span>
                    ))}
                  </div>
                )}
                {paint.locationNotes && (
                  <p className="cs-paint-notes-line">{paint.locationNotes}</p>
                )}
                {paint.adminEdited && (
                  <div className="cs-card-footer">
                    <AdminBadge adminEdit={paint.adminEdited} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="cs-empty-state">
        <FaPaintBrush />
        <h4>No paint selections yet</h4>
        <p>Add your paint selections using the button above</p>
      </div>
    )}
  </div>
);

// ============================================================================
// OWNER PURCHASES TAB
// ============================================================================
/**
 * Map delivery method values to color-coded pill class modifiers.
 * @param {string} method
 * @returns {string} class modifier suffix
 */
const getDeliveryPillClass = (method) => {
  switch (method) {
    case "Owner-delivered to Jobsite":
      return "cs-delivery-pill--jobsite";
    case "Owner-delivered to Project Manager":
      return "cs-delivery-pill--pm";
    case "Delivery Driver":
      return "cs-delivery-pill--driver";
    case "Other":
      return "cs-delivery-pill--other";
    default:
      return "cs-delivery-pill--default";
  }
};

/**
 * Owner purchases tab with add/edit capability and image viewing
 * @param {Object} props
 * @param {Array} props.purchases - Array of owner purchase objects
 * @param {Function} props.onAdd - Handler to open add purchase modal
 * @param {Function} props.onEdit - Handler to open edit purchase modal
 * @param {Function} props.onImageClick - Handler for image thumbnail clicks
 */
const OwnerPurchasesTab = ({ purchases, onAdd, onEdit, onImageClick }) => (
  <div className="cs-active-container">
    <div className="cs-section-header-row">
      <h3 className="cs-section-header">Owner-Purchased Materials</h3>
      <button className="cs-btn-add" onClick={onAdd}>
        <FaPlus /> Add Purchase
      </button>
    </div>
    {purchases.length > 0 ? (
      <div className="cs-cards-grid">
        {purchases.map((purchase) => {
          const firstPhoto = purchase.jobsitePhoto?.[0];
          const photoUrl = firstPhoto?.url || firstPhoto?.thumbnail;
          const deliveryMethod = purchase.deliveryMethod;
          const productUrl = purchase.productLink;
          return (
            <div key={purchase.id} className="cs-purchase-card">
              <div
                className="cs-purchase-image-slot"
                onClick={
                  firstPhoto ? () => onImageClick(firstPhoto) : undefined
                }
                style={firstPhoto ? { cursor: "pointer" } : undefined}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={purchase.productName}
                    className="cs-purchase-image"
                  />
                ) : (
                  <div className="cs-purchase-image-placeholder">
                    <FaImage />
                  </div>
                )}
                <div
                  className="cs-card-actions-corner"
                  onClick={(e) => e.stopPropagation()}
                >
                  <RowActionsMenu
                    onEdit={() => onEdit(purchase)}
                    editLabel="Edit Purchase"
                  />
                </div>
              </div>
              <div className="cs-purchase-card-body">
                <div className="cs-purchase-header">
                  <h4 className="cs-purchase-name">{purchase.productName}</h4>
                  {deliveryMethod && (
                    <span
                      className={`cs-purchase-delivery-pill ${getDeliveryPillClass(
                        deliveryMethod,
                      )}`}
                    >
                      {deliveryMethod}
                    </span>
                  )}
                </div>
                <div className="cs-purchase-meta-grid">
                  <div className="cs-purchase-meta-item">
                    <span className="cs-purchase-meta-label">Qty</span>
                    <span className="cs-purchase-meta-value">
                      {purchase.quantity || 1}
                    </span>
                  </div>
                  <div className="cs-purchase-meta-item">
                    <span className="cs-purchase-meta-label">Room</span>
                    <span className="cs-purchase-meta-value">
                      {purchase.roomPlacement || "—"}
                    </span>
                  </div>
                  <div className="cs-purchase-meta-item">
                    <span className="cs-purchase-meta-label">
                      Delivery Date
                    </span>
                    <span className="cs-purchase-meta-value">
                      {purchase.deliveryDate
                        ? formatDateET(purchase.deliveryDate)
                        : "—"}
                    </span>
                  </div>
                  <div className="cs-purchase-meta-item">
                    <span className="cs-purchase-meta-label">Notes</span>
                    <span className="cs-purchase-meta-value">
                      {purchase.notes || "—"}
                    </span>
                  </div>
                </div>
                <div className="cs-paint-divider" />
                <div className="cs-purchase-card-footer">
                  {productUrl ? (
                    <a
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cs-purchase-product-link"
                    >
                      View product <FaExternalLinkAlt />
                    </a>
                  ) : (
                    <span />
                  )}
                  <AdminBadge adminEdit={purchase.adminEdited} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="cs-empty-state">
        <FaShoppingCart />
        <h4>No Owner Purchases</h4>
        <p>Your owner-purchased materials will appear here once submitted.</p>
      </div>
    )}
  </div>
);

// ============================================================================
// DOCUMENTS TAB
// ============================================================================

/**
 * Documents tab showing selection documents organized by category
 * Only categories that actually contain documents are rendered; when the
 * client has no documents at all an empty state is shown instead.
 * @param {Object} props
 * @param {Object} props.documents - Documents object keyed by category
 * @param {Function} props.onDocClick - Handler for document click
 */
const DocumentsTab = ({ documents, onDocClick }) => {
  const populatedCategories = DOCUMENT_CATEGORIES.map((category) => ({
    ...category,
    docs: documents?.[category.key] || [],
  })).filter((category) => category.docs.length > 0);

  return (
    <div className="cs-active-container">
      <div className="cs-section-header-row">
        <h3 className="cs-section-header">Vendor Quotes & Invoices</h3>
      </div>
      {populatedCategories.length > 0 ? (
        <div className="cs-documents-grid">
          {populatedCategories.map((category) => (
            <div key={category.key} className="cs-document-category">
              <h4 className="cs-document-category-title">
                <FaFilePdf /> {category.label}
              </h4>
              <div className="cs-document-list">
                {category.docs.map((doc, index) => (
                  <div
                    key={doc.id || index}
                    className="cs-document-card"
                    onClick={() => onDocClick(doc)}
                  >
                    <div className="cs-document-preview">
                      {doc.type === "application/pdf" ? (
                        <FaFilePdf className="cs-doc-icon" />
                      ) : doc.thumbnail ? (
                        <img src={doc.thumbnail} alt={doc.name} />
                      ) : (
                        <FaFileAlt className="cs-doc-icon" />
                      )}
                    </div>
                    <div className="cs-document-meta">
                      <p className="cs-document-name">{doc.name}</p>
                      <span className="cs-document-type">
                        {getFileTypeLabel(doc)}
                      </span>
                    </div>
                    <button type="button" className="cs-view-doc-btn">
                      View <FaExternalLinkAlt />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="cs-empty-state">
          <FaFolderOpen />
          <h4>No documents yet</h4>
          <p>
            Vendor quotes and invoices will appear here once they've been added
            to your project.
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DOCUMENT VIEWER MODAL
// ============================================================================

/**
 * Modal for viewing PDF and image documents
 * @param {Object} props
 * @param {Object} props.doc - Document object with url, name, type
 * @param {Function} props.onClose - Close handler
 */
const DocumentViewerModal = ({ doc, onClose }) => {
  if (!doc) return null;

  const isPDF = doc.type?.includes("pdf") || doc.filename?.endsWith(".pdf");
  const isImage =
    doc.type?.includes("image") ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.filename || doc.name || "");

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="cs-modal-header">
          <h3>{doc.filename || doc.name || "Document"}</h3>
          <div className="cs-modal-actions">
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cs-modal-action-btn"
              title="Open in new tab"
            >
              <FaExternalLinkAlt />
            </a>
            <a
              href={doc.url}
              download={doc.filename || doc.name}
              className="cs-modal-action-btn"
              title="Download"
            >
              <FaDownload />
            </a>
            <button
              className="cs-modal-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="cs-modal-content">
          {isPDF ? (
            <iframe
              src={doc.url}
              title={doc.filename || "PDF Document"}
              className="cs-modal-pdf"
            />
          ) : isImage ? (
            <img
              src={doc.url}
              alt={doc.filename || "Image"}
              className="cs-modal-image"
            />
          ) : (
            <div className="cs-modal-unsupported">
              <FaFilePdf />
              <p>Preview not available for this file type.</p>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cs-modal-download-link"
              >
                <FaDownload /> Download to view
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Client Selections page component
 * Provides tabbed interface for viewing and managing home selections
 */
function ClientSelections() {
  const [activeTab, setActiveTab] = useState("overview");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageModal, setImageModal] = useState(null);
  const [documentModal, setDocumentModal] = useState(null);
  // Paint / OPM modal state ({ isOpen, initialData }); initialData is
  // null for add mode, or the row being edited for edit mode.
  const [paintModal, setPaintModal] = useState({
    isOpen: false,
    initialData: null,
  });
  const [opmModal, setOpmModal] = useState({
    isOpen: false,
    initialData: null,
  });

  // Get project data
  const {
    data: response,
    isLoading: projectLoading,
    error: projectError,
  } = useClientProject();

  const project = response?.data || response;

  // Get selections data via shared hook
  const {
    data: selectionsData,
    isLoading: selectionsLoading,
    error: selectionsError,
    refetch: refetchSelections,
  } = useClientSelectionsData(project?.id);

  // Soft delete mutation
  const markSelectionRemoved = useMarkSelectionRemoved();

  // Inline edit mutation
  const updateSelectionsMutation = useUpdateClientSelections();

  // Fetch field options for edit mode dropdowns (cached 30min)
  const { data: fieldOptions } = useSelectionFieldOptions();

  // Extract data - handle both nested and flat response formats
  const clientSelections =
    selectionsData?.data?.clientSelections || selectionsData?.clientSelections;
  const paintSelections =
    selectionsData?.data?.paintSelections ||
    selectionsData?.paintSelections ||
    [];
  const ownerPurchases =
    selectionsData?.data?.ownerPurchases ||
    selectionsData?.ownerPurchases ||
    [];
  const vendors =
    selectionsData?.data?.vendors || selectionsData?.vendors || [];

  // Combined loading/error state
  const isLoading = projectLoading || selectionsLoading;
  const error = projectError || selectionsError;

  // ========== LOADING STATE ==========
  if (isLoading) {
    return (
      <>
        <div className="component-loading">
          <div className="component-spinner"></div>
          <p>Loading your selections...</p>
        </div>
      </>
    );
  }

  // ========== ERROR STATE ==========
  if (error) {
    return (
      <>
        <div className="dashboard-error">
          <FaExclamationTriangle />
          <h2>Unable to Load Selections</h2>
          <p>{projectError?.message || "An error occurred"}</p>
          <button onClick={() => refetchSelections()}>Try Again</button>
        </div>
      </>
    );
  }

  // ========== NO PROJECT STATE ==========
  if (!project || !project.id) {
    return (
      <>
        <div className="state-container">
          <FaHome className="state-icon" />
          <h2 className="state-title">Welcome to My Red Setter Home</h2>
          <p className="state-message">
            No active project found for your account.
          </p>
          <p className="state-message">
            Please contact your project manager for assistance.
          </p>
        </div>
      </>
    );
  }

  // ========== DERIVED STATE ==========
  const daysRemaining = calculateDaysRemaining(
    clientSelections?.selectionsDueDate || project?.selectionsDueDate,
  );
  const isPastDeadline = daysRemaining !== null && daysRemaining < 0;

  /**
   * Handle confirmed soft deletion of a selection
   */
  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await markSelectionRemoved.mutateAsync({
        type: deleteConfirm.type,
        id: deleteConfirm.id,
        projectId: project.id,
      });
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error removing selection:", err);
    }
  };

  // ========== RENDER ==========
  return (
    <>
      <div className="client-selections">
        <div className="page-container">
          {/* Page Header */}
          <div className="page-header">
            <h1 className="page-title">Home Selections</h1>
            <p className="page-subtitle">Customize your home</p>
          </div>

          {/* Selection Deadline Alert */}
          <div
            className={`cs-deadline-alert ${
              isPastDeadline
                ? "cs-deadline-alert--danger"
                : "cs-deadline-alert--success"
            }`}
          >
            <div className="cs-alert-icon">
              {isPastDeadline ? <FaExclamation /> : <FaClock />}
            </div>
            <div className="cs-alert-content">
              <h4>Selection Deadline</h4>
              {daysRemaining !== null ? (
                isPastDeadline ? (
                  <p>
                    Selection deadline has passed! Your 45-day deadline was
                    <b>
                      {" "}
                      {Math.abs(daysRemaining)} days ago (
                      {formatDateET(
                        clientSelections?.selectionsDueDate ||
                          project?.selectionsDueDate,
                      )}
                      )
                    </b>
                    . To make changes or additional selections, please contact
                    your project manager to make a change order.
                  </p>
                ) : (
                  <p>
                    <b>{daysRemaining} days</b> remaining to submit your home
                    selections (due{" "}
                    {formatDateET(
                      clientSelections?.selectionsDueDate ||
                        project?.selectionsDueDate,
                    )}
                    ).
                  </p>
                )
              ) : (
                <p>No deadline set. Please contact your project manager.</p>
              )}
            </div>
          </div>
          <div className="sp-actions-bar">
            <button className="sp-print-btn" onClick={handlePrintSelections}>
              <FaPrint /> Print Selections
            </button>
            <button
              className="sp-download-btn"
              onClick={() =>
                handleDownloadCSV(
                  clientSelections,
                  paintSelections,
                  project?.projectName || project?.projectAddress,
                )
              }
            >
              <FaFileDownload /> Download CSV
            </button>
          </div>

          {/* Tabs */}
          <div className="cs-tabs-container">
            <TabNav
              tabs={SELECTION_TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            <div className="cs-tab-content">
              {/* Overview Tab - NOW WITH EDIT SUPPORT */}
              {activeTab === "overview" && (
                <OverviewTab
                  data={clientSelections}
                  recordId={clientSelections?.id}
                  projectId={project.id || project}
                  updateMutation={updateSelectionsMutation}
                  fieldOptions={fieldOptions}
                  isPastDeadline={isPastDeadline}
                />
              )}

              {/* Paint Tab */}
              {activeTab === "paint" && (
                <PaintTab
                  paintSelections={paintSelections}
                  onAdd={() =>
                    setPaintModal({ isOpen: true, initialData: null })
                  }
                  onEdit={(paint) =>
                    setPaintModal({ isOpen: true, initialData: paint })
                  }
                  onDelete={setDeleteConfirm}
                  isPastDeadline={isPastDeadline}
                />
              )}

              {/* Owner Purchases Tab */}
              {activeTab === "purchases" && (
                <OwnerPurchasesTab
                  purchases={ownerPurchases}
                  onAdd={() => setOpmModal({ isOpen: true, initialData: null })}
                  onEdit={(purchase) =>
                    setOpmModal({ isOpen: true, initialData: purchase })
                  }
                  onImageClick={setImageModal}
                />
              )}

              {/* Documents Tab */}
              {activeTab === "documents" && (
                <DocumentsTab
                  documents={clientSelections?.documents || {}}
                  onDocClick={setDocumentModal}
                />
              )}
            </div>
          </div>

          {/* Vendor Information Section */}
          <h2 className="cs-vendor-heading">Vendor Information</h2>
          <p className="cs-vendor-subheading">
            You may contact these vendors to make your selections.
          </p>
          <p className="cs-vendor-alert">
            Please DO NOT purchase anything directly from these vendors. Make
            your selections with them and they will send us quotes. We will
            confirm purchases with you before placing any orders for your
            project.
          </p>
          <div className="cs-vendor-section">
            <div className="cs-vendor-cards-grid">
              {vendors
                .filter(
                  (vendor) =>
                    vendor.status === "Active" && vendor.isClientFacing,
                )
                .map((vendor) => (
                  <div
                    key={vendor.id}
                    className="cs-vendor-card"
                    data-vendor-categories={
                      vendor.vendorCategories?.join(",") || ""
                    }
                  >
                    {vendor.logo && (
                      <div className="cs-vendor-logo">
                        <img src={vendor.logo} alt={vendor.vendorName} />
                      </div>
                    )}
                    <h3 className="cs-vendor-name">{vendor.vendorName}</h3>
                    <div className="cs-vendor-categories">
                      {vendor.vendorCategories?.map((cat, index) => (
                        <span key={index} className="cs-vendor-pill">
                          {cat}
                        </span>
                      ))}
                    </div>
                    {vendor.address && (
                      <p className="cs-vendor-info">
                        <FaMapMarkerAlt />
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(
                            vendor.address,
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {vendor.address}
                        </a>
                      </p>
                    )}
                    {vendor.contactName && (
                      <p className="cs-vendor-info">
                        <FaUser />
                        {vendor.contactName}
                      </p>
                    )}
                    {vendor.contactPhone && (
                      <p className="cs-vendor-info">
                        <FaPhone />
                        <a href={`tel:${vendor.contactPhone}`}>
                          {vendor.contactPhone}
                        </a>
                      </p>
                    )}
                    {vendor.contactEmail && (
                      <p className="cs-vendor-info">
                        <FaEnvelope />
                        <a href={`mailto:${vendor.contactEmail}`}>
                          {vendor.contactEmail}
                        </a>
                      </p>
                    )}
                    {vendor.website && (
                      <p className="cs-vendor-info">
                        <FaGlobe />
                        <a
                          href={vendor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Visit Website
                        </a>
                      </p>
                    )}
                    {vendor.notes && (
                      <>
                        <div className="cs-vendor-divider"></div>
                        <p className="cs-vendor-notes">{vendor.notes}</p>
                      </>
                    )}
                  </div>
                ))}
            </div>
            {vendors.filter(
              (vendor) => vendor.status === "Active" && vendor.isClientFacing,
            ).length === 0 && (
              <div className="cs-no-vendors">
                <p>No vendor information available at this time.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========== MODALS ========== */}

      {/* Image Modal */}
      {imageModal && (
        <div className="cs-modal-overlay" onClick={() => setImageModal(null)}>
          <div className="cs-image-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="cs-modal-close-btn"
              onClick={() => setImageModal(null)}
            >
              <FaTimes />
            </button>
            <img
              src={imageModal.url || imageModal}
              alt={imageModal.filename || "Selection Image"}
            />
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {documentModal && (
        <DocumentViewerModal
          doc={documentModal}
          onClose={() => setDocumentModal(null)}
        />
      )}

      {/* Add / Edit Paint Modal */}
      <AddPaintModal
        isOpen={paintModal.isOpen}
        onClose={() => setPaintModal({ isOpen: false, initialData: null })}
        projectId={project.id || project}
        mode={paintModal.initialData ? "edit" : "add"}
        initialData={paintModal.initialData}
      />

      {/* Add / Edit Owner Purchase Modal */}
      <AddOPMModal
        isOpen={opmModal.isOpen}
        onClose={() => setOpmModal({ isOpen: false, initialData: null })}
        projectId={project?.id || project}
        mode={opmModal.initialData ? "edit" : "add"}
        initialData={opmModal.initialData}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="cs-modal-overlay"
          onClick={() => setDeleteConfirm(null)}
        >
          <div className="cs-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Remove Selection</h3>
            <p>
              Are you sure you want to remove this selection from your record?
            </p>
            <div className="cs-delete-modal-actions">
              <button
                className="cs-btn-cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="cs-btn-danger"
                onClick={confirmDelete}
                disabled={markSelectionRemoved.isLoading}
              >
                {markSelectionRemoved.isLoading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== PRINT LAYOUT (hidden on screen) ========== */}
      <SelectionsPrintLayout
        selections={clientSelections}
        paintSelections={paintSelections}
        projectName={project?.projectName || project?.projectAddress}
      />
    </>
  );
}

export default ClientSelections;
